import React from 'react';
import { useWorldStore } from '../state/worldStore';

// VRStage — what happens to flat UI when the screen becomes two screens.
//
// ── WHY A DOM OVERLAY IS ACTIVELY DISORIENTING IN SPLIT VIEW ─────────────────
// It is ONE element spanning BOTH eyes. The left eye sees its left portion and
// the right eye sees its right portion, so there is no matching pair for the
// brain to fuse — it reads as an object at an impossible depth, sitting across
// the seam. That is not a cosmetic problem: mismatched stereo is one of the
// reliable ways to make someone feel ill, and it gets worse the longer you look.
//
// Two answers, and both are needed.
//
//   <VRHide>  everything that is not essential simply goes. A chat dock, an
//             admin panel and a story card have no business in a headset, and
//             hiding them costs nothing because you can take the phone out.
//
//   <VRSafe>  the few things you cannot lose get drawn TWICE, once inside each
//             eye's half, so each eye sees its own complete copy at the same
//             place. That is what makes UI fusable rather than merely present.
//
// ── THE TRICK THAT MAKES VRSafe WORK ─────────────────────────────────────────
// Every overlay in this app is `position: fixed` against the viewport, so simply
// putting it in a half-width div would change nothing — fixed ignores ordinary
// ancestors. But a `transform` on an ancestor makes that ancestor the containing
// block for fixed descendants. So each eye's wrapper carries a transform, and
// `right: 16px` inside it resolves to 16px from THAT HALF's edge, with no change
// to a single overlay component.
const eye = (side) => ({
  position: 'fixed', top: 0, [side]: 0, width: '50%', height: '100%',
  // Establishes the containing block. Also promotes to its own layer, which is
  // what we want for something composited over a WebGL canvas every frame.
  transform: 'translateZ(0)',
  overflow: 'hidden', pointerEvents: 'none', zIndex: 90,
});
// The children themselves re-enable pointers; the wrapper must not swallow
// taps meant for the canvas underneath.
const inner = { position: 'absolute', inset: 0, pointerEvents: 'none' };

export function VRHide({ children }) {
  const stereo = useWorldStore((s) => s.stereo);
  // `contents` rather than a wrapping box: the children are all position:fixed
  // and a real box in the tree would create a containing block and move them.
  return <div style={{ display: stereo ? 'none' : 'contents' }}>{children}</div>;
}

export function VRSafe({ children }) {
  const stereo = useWorldStore((s) => s.stereo);
  if (!stereo) return <>{children}</>;
  // Rendered twice, deliberately. Both copies are live: they read the same store
  // and write the same bridges, so tapping either works and neither holds state
  // the other needs.
  return (
    <>
      <div style={eye('left')}><div style={inner}>{children}</div></div>
      <div style={eye('right')}><div style={inner}>{children}</div></div>
    </>
  );
}

export default VRHide;
